/**
 * Check-in page route
 *
 * Protected route for logging pain entries.
 * Uses the PainEntryForm component connected to the API.
 * Supports date selection for logging past entries.
 * Accepts optional ?date=YYYY-MM-DD search param to pre-populate the date.
 *
 * Duplicate-day detection: when a user tries to add a check-in for a date
 * that already has a record, we automatically redirect to edit mode so the
 * existing entry is loaded for editing instead of creating a duplicate.
 */
import { useState, useCallback, useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CheckCircle, AlertCircle, Loader2, Pencil, WifiOff } from "lucide-react";
import { PainEntryForm } from "@/components/pain-entry";
import { useOfflinePainEntry, useDailyRecord } from "@/hooks";
import type { CreatePainEntryRequest } from "@/types/pain-entry";
import { formatDateForApi, decodeStructuredTags, getUserVisibleTags } from "@/types/pain-entry";
import type { DailyRecordResponse } from "@floline/api-client";
import {
  validateCheckInSearchParams,
  getInitialDateFromSearchParams,
} from "./-check-in-search-params";

function createInitialValues(record: DailyRecordResponse) {
  // Decode structured symptom and activity tags.
  const allTags = record.tags ?? [];
  const structuredData = decodeStructuredTags(allTags);
  const userTags = getUserVisibleTags(allTags);

  return {
    painLocations: record.painLocations.map((loc) => ({
      zone: loc.zone,
      side: loc.side,
      surface: loc.surface,
      region: loc.region,
      laterality: loc.laterality,
      intensityScore: loc.intensityScore,
      detail: loc.detail ?? undefined,
      qualities: loc.qualities ?? undefined,
    })),
    medications: (record.medications ?? []).map((med) => ({
      name: med.name,
      doseValue: med.doseValue ?? undefined,
      doseUnit: med.doseUnit ?? undefined,
      timeOfDay: med.timeOfDay,
      notes: med.notes ?? undefined,
      userMedicationId: med.userMedicationId ?? undefined,
      relatedEventId: med.relatedEventId ?? undefined,
      fromActiveMedication: med.userMedicationId ? true : undefined,
    })),
    notes: record.notes ?? undefined,
    tags: userTags.length > 0 ? userTags : undefined,
    // Functional impact from dedicated table
    functionalImpact: record.functionalImpact
      ? {
          generalActivity: record.functionalImpact.generalActivity ?? undefined,
          mood: record.functionalImpact.mood ?? undefined,
          mobility: record.functionalImpact.mobility ?? undefined,
          work: record.functionalImpact.work ?? undefined,
          relationships: record.functionalImpact.relationships ?? undefined,
          sleep: record.functionalImpact.sleep ?? undefined,
          enjoymentOfLife: record.functionalImpact.enjoymentOfLife ?? undefined,
          notes: record.functionalImpact.notes ?? undefined,
        }
      : undefined,
    // Structured symptom and activity data
    fatigue: structuredData.fatigue ?? undefined,
    cognitiveFog: structuredData.cognitiveFog ?? undefined,
    nausea: structuredData.nausea ?? undefined,
    vomiting: structuredData.vomiting ?? undefined,
    weakness: structuredData.weakness ?? undefined,
    activityPacing: structuredData.activityPacing ?? undefined,
  };
}

/**
 * Check-in page component
 *
 * Displays the pain entry form and handles API submission.
 * Uses the useOfflinePainEntry hook for offline queue support and cache invalidation.
 * Pre-populates date from ?date=YYYY-MM-DD search param if provided.
 */
function CheckInPage() {
  const { t } = useTranslation("checkin");
  const navigate = useNavigate();
  const search = Route.useSearch();
  const isEditMode = search.edit === true;
  const initialDate = getInitialDateFromSearchParams(search);
  const targetDate = search.date ?? formatDateForApi(initialDate);
  const todayDate = formatDateForApi(new Date());
  const isEditingPastEntry = isEditMode && targetDate < todayDate;
  const [isSuccess, setIsSuccess] = useState(false);
  /** Track whether the successful save was queued offline */
  const wasQueuedRef = useRef(false);
  /** Ref for the success heading — focused for screen reader announcement */
  const successHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const { createPainEntry, isCreating, isOnline, error } = useOfflinePainEntry();

  // Always fetch the daily record for the target date so we can detect
  // duplicates in both create mode and edit mode.
  const { record: existingRecord, isLoading: isLoadingExisting } = useDailyRecord(targetDate);

  // Duplicate-day redirect: if we're in create mode (not edit) and a record
  // already exists for the target date, redirect to edit mode automatically.
  useEffect(() => {
    if (!isEditMode && existingRecord && !isLoadingExisting) {
      void navigate({
        to: "/check-in",
        search: { date: targetDate, edit: true },
        replace: true,
      });
    }
  }, [isEditMode, existingRecord, isLoadingExisting, navigate, targetDate]);

  // If URL is forced to edit mode but no record exists for that date,
  // downgrade back to create mode to avoid contradictory "editing" UI.
  useEffect(() => {
    if (isEditMode && !existingRecord && !isLoadingExisting) {
      void navigate({
        to: "/check-in",
        search: { date: targetDate },
        replace: true,
      });
    }
  }, [isEditMode, existingRecord, isLoadingExisting, navigate, targetDate]);

  const shouldPrefill = isEditMode && Boolean(search.date);
  const initialValues = shouldPrefill && existingRecord ? createInitialValues(existingRecord) : undefined;

  /** Handle form submission */
  const handleSubmit = useCallback(
    async (data: CreatePainEntryRequest) => {
      try {
        // Track whether we're offline before the call so the success
        // screen can show an appropriate hint.
        const offlineAtSubmit = !isOnline;
        await createPainEntry(data);

        wasQueuedRef.current = offlineAtSubmit;

        // Show success state
        setIsSuccess(true);

        // Navigate after a brief delay
        setTimeout(() => {
          if (isEditMode) {
            navigate({ to: "/daily/$date", params: { date: targetDate } });
            return;
          }
          navigate({ to: "/" });
        }, 1500);
      } catch {
        // Error is handled by the hook and available in error state
      }
    },
    [createPainEntry, isOnline, isEditMode, navigate, targetDate]
  );

  /** Handle cancel */
  const handleCancel = useCallback(() => {
    if (isEditMode) {
      navigate({ to: "/daily/$date", params: { date: targetDate } });
      return;
    }
    navigate({ to: "/" });
  }, [isEditMode, navigate, targetDate]);

  /**
   * Handle date change from the form's date picker.
   * When the user picks a new date, navigate to the check-in page with
   * the new date. If a record already exists for that date, the useEffect
   * above will redirect to edit mode on the next render.
   */
  const handleDateChange = useCallback(
    (newDate: Date) => {
      const newDateStr = formatDateForApi(newDate);
      // Navigate to the same page with the new date, resetting edit mode.
      // The useEffect duplicate detection will upgrade to edit mode if needed.
      void navigate({
        to: "/check-in",
        search: { date: newDateStr },
        replace: true,
      });
    },
    [navigate]
  );

  // While we're about to redirect, show a loading state instead of the form
  // to avoid a flash of the create form before the edit redirect.
  if (!isEditMode && existingRecord && !isLoadingExisting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/60" />
        <p className="text-sm text-muted-foreground">{t("loadingExisting")}</p>
      </div>
    );
  }

  // While downgrading from invalid edit mode to create mode, keep UI stable.
  if (isEditMode && !existingRecord && !isLoadingExisting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/60" />
        <p className="text-sm text-muted-foreground">{t("loadingExisting")}</p>
      </div>
    );
  }

  // Show success message -- polished V1 success state
  // Auto-focus the heading so screen readers announce the outcome (WCAG 4.1.2)
  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6" role="status">
        <div className="flex flex-col items-center gap-5 animate-in fade-in-0 zoom-in-95 duration-500">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
            <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          </div>
          <div className="text-center space-y-2">
            <h2
              ref={(el) => {
                successHeadingRef.current = el;
                // Focus the heading on mount so screen readers announce success
                el?.focus();
              }}
              tabIndex={-1}
              className="text-2xl font-bold tracking-tight outline-none"
            >
              {isEditMode ? t("successUpdated") : t("successSaved")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isEditMode ? t("redirectDayView") : t("redirectDashboard")}
            </p>
            {wasQueuedRef.current && (
              <p className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <WifiOff className="h-3.5 w-3.5" />
                {t("queuedOfflineHint")}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Loading state for pre-fill in edit mode, or while checking for duplicate
  if ((shouldPrefill || isLoadingExisting) && isLoadingExisting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/60" />
        <p className="text-sm text-muted-foreground">{t("loadingExisting")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Edit mode banner -- refined V1 treatment */}
      {isEditMode && (
        <div className="mx-auto w-full max-w-2xl md:max-w-4xl lg:max-w-screen-xl rounded-lg border border-sky-200/80 dark:border-sky-800/60 bg-sky-50/80 dark:bg-sky-950/30 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-sky-100 dark:bg-sky-900/50">
              <Pencil className="h-3.5 w-3.5 text-sky-700 dark:text-sky-300" />
            </div>
            <p className="text-sm leading-relaxed text-sky-900 dark:text-sky-200">
              {t("editBanner", { date: targetDate })}
            </p>
          </div>
        </div>
      )}

      {/* Error message -- V1 error treatment */}
      {error && (
        <div className="rounded-lg border border-red-200/80 dark:border-red-800/60 bg-red-50/80 dark:bg-red-950/30 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-red-100 dark:bg-red-900/50">
              <AlertCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-sm leading-relaxed text-red-800 dark:text-red-200">
              {error.message || t("errorDefault")}
            </p>
          </div>
        </div>
      )}

      {/* Pain entry form with date picker enabled */}
      <PainEntryForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDateChange={handleDateChange}
        isSubmitting={isCreating}
        showDatePicker={!isEditMode}
        showBodyDiagram={true}
        date={initialDate}
        initialValues={initialValues}
        hideCopyFromYesterday={isEditMode}
        isEditingPastEntry={isEditingPastEntry}
        isEditMode={isEditMode}
        editDateLabel={targetDate}
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/check-in")({
  component: CheckInPage,
  validateSearch: validateCheckInSearchParams,
});
